import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  approveRegisterRequest,
  getRegisterRequest,
  investigateRegisterRequest,
  RegisterRequestHistoryInterface,
  RegisterRequestShowInterface,
  requestCompletionRegisterRequest,
  resetRegisterRequest,
} from './registerRequestApi';
import RegisterRequestEmailHistoryDialog from './components/RegisterRequestEmailHistoryDialog';
import FileViewer from './components/FileViewer';
import { BackLink } from '../Common/ButtonLinks';
import { Badge, Button, Tooltip, Typography } from '@mui/material';
import FormFreeDateField from '../Common/FormFreeItems/FormFreeDateField';
import { Handshake, HandshakeOutlined, ThumbUpAltOutlined, ForumOutlined, GavelOutlined } from '@mui/icons-material';
import { Formik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { getDefaultsWithActualValues } from '../Common/utilities';
import * as Yup from 'yup';
import InvestigationDialog from './components/InvestigationDialog';
import { useNotify } from '../Common/snackbarHooks';
import ApproveWarningDialog from './components/ApproveWarningDialog';
import RequireCompletionDialog from './components/RequireCompletionDialog';
import { stringToPositiveInt, useQuery } from '../Common/utilities';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import ResetDialog from './components/ResetDialog';

const registerRequestInitialValues = {
  valid_from: null as Dayjs | null,
};

type InfoRowProps = {
  label: string;
  value?: string;
  strong?: boolean;
};

const InfoRow = ({ label, value, strong = false }: InfoRowProps) => {
  const isMissingPersonnummer = label === 'Personnummer:' && !value;

  return (
    <div className="grid grid-cols-[0.4fr_0.6fr] gap-1">
      <Typography className="!text-neutral-600">{label}</Typography>

      <Tooltip
        title={isMissingPersonnummer ? 'Användarprofilen saknar personnummer' : ''}
        disableHoverListener={!isMissingPersonnummer}
      >
        <div className={isMissingPersonnummer ? 'border-4 border-red-400 min-h-6' : ''}>
          <Typography className={`!text-neutral-600 ${strong ? '!font-bold' : '!font-medium'}`}>
            {value || ''}
          </Typography>
        </div>
      </Tooltip>
    </div>
  );
};

export default function EditRegisterRequest() {
  const { id } = useParams<{ id: string }>();
  const registerRequestId = Number(id);

  const location = useLocation();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotify();

  const [registerRequest, setRegisterRequest] = useState<RegisterRequestShowInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [personNrCorrect, setPersonNrCorrect] = useState<boolean>(false);
  const [allFilesChecked, setAllFilesChecked] = useState<boolean>(false);
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [oldValidFrom, setOldValidFrom] = useState<boolean>(false);

  const [approveWarningDialogVisible, setApproveWarningDialogVisible] = useState(false);
  const [requireCompletionDialogVisible, setRequireCompletionDialogVisible] = useState(false);
  const [investigationDialogVisible, setInvestigationDialogVisible] = useState(false);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [emailHistoryDialogVisible, setEmailHistoryDialogVisible] = useState(false);

  const [query, setQuery] = useQuery(
    {
      search: (val) => val,
      operationalManagers: (val) => val,
      residenceGroups: (val) => val,
      toDate: (val) => (dayjs(val, 'YYYY-MM-DD').isValid() ? dayjs(val, 'YYYY-MM-DD') : undefined),
      showNotHandled: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showRequiresCompletion: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showUnderInvestigation: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showApproved: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionValidPrevious: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionUnderFourteen: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionNotRequired: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showExceptionMigration: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      showComplete: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
      sortBy: (val) => val,
      sortOrder: (val) => (val === 'asc' ? ('asc' as const) : val === 'desc' ? ('desc' as const) : undefined),
      page: (val) => stringToPositiveInt(val),
    },
    { backConverter: { toDate: (val) => val?.format('YYYY-MM-DD') } }
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;
      const tag = activeElement?.tagName;

      if (tag === 'INPUT' || tag === 'TEXTAREA' || activeElement?.isContentEditable) {
        return;
      }

      if (event.key === 'ArrowUp' && registerRequest?.previous_id) {
        event.preventDefault();

        navigate(`/registry-control/${registerRequest.previous_id}${location.search}`);
      }

      if (event.key === 'ArrowDown' && registerRequest?.next_id) {
        event.preventDefault();

        navigate(`/registry-control/${registerRequest.next_id}${location.search}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [registerRequest?.previous_id, registerRequest?.next_id, location.search, navigate]);

  useEffect(() => {
    if (!registerRequestId) return;

    getRegisterRequest(registerRequestId, {
      filter_operational_managers: query.operationalManagers?.split(',').map((id) => id),
      filter_residence_groups: query.residenceGroups?.split(',').map((id) => stringToPositiveInt(id)),
      filter_to_date: query.toDate?.format('YYYY-MM-DD'),
      show_complete: query.showComplete ?? true,
      show_not_handled: query.showNotHandled ?? true,
      show_requires_completion: query.showRequiresCompletion ?? false,
      show_under_investigation: query.showUnderInvestigation ?? false,
      show_approved: query.showApproved ?? false,
      show_exception_valid_previous: query.showApproved ? query.showExceptionValidPrevious ?? true : false,
      show_exception_under_fourteen: query.showApproved ? query.showExceptionUnderFourteen ?? true : false,
      show_exception_not_required: query.showApproved ? query.showExceptionNotRequired ?? true : false,
      show_exception_migration: query.showApproved ? query.showExceptionMigration ?? true : false,
      sort_by: query.sortBy,
      sort_order: query.sortOrder,
    })
      .then((response) => {
        setRegisterRequest(response.data.data);
        setPersonNrCorrect(response.data.data.assessment === 'Godkänd');
      })
      .catch(() => {
        setError('Could not load register request');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [registerRequestId, query]);

  function isOlderThanSixMonths(day: Dayjs | null): boolean {
    return !!day && day.isBefore(dayjs().subtract(6, 'month'), 'date');
  }

  async function handleSubmit(values: { valid_from: Dayjs | null }, setSubmitting: (isSubmitting: boolean) => void) {
    if (!registerRequest || !values.valid_from) return;

    setSubmitting(true);

    setValidFrom(values.valid_from);
    setOldValidFrom(isOlderThanSixMonths(values.valid_from));

    if (allFilesChecked) {
      approveRequest(registerRequestId, values.valid_from.format('YYYY-MM-DD')).finally(() => setSubmitting(false));
    } else {
      setApproveWarningDialogVisible(true);
      setSubmitting(false);
    }
  }

  async function approveRequest(registerRequestId: number, valid_from?: string) {
    if (!valid_from) return;

    await approveRegisterRequest(registerRequestId, {
      valid_from: valid_from,
    })
      .then(() => notifySuccess('Registerutdraget har godkänts.'))
      .then(() => {
        if (registerRequest?.next_id && registerRequestId !== registerRequest.next_id) {
          navigate(`/registry-control/${registerRequest.next_id}?${location.search}`);
        } else {
          navigate(`/registry-control?${location.search}`);
        }
      })
      .catch((error) => notifyError(`Det gick inte att godkänna registerutdraget.`));
  }

  const acceptRequireCompletion = (text: string) => {
    requestCompletionRequest(registerRequestId, text);
  };

  async function requestCompletionRequest(registerRequestId: number, text: string) {
    await requestCompletionRegisterRequest(registerRequestId, { text: text })
      .then(() => notifySuccess('Registerutdraget har granskats (uppgifter saknas).'))
      .then(() => {
        if (registerRequest?.next_id && registerRequestId !== registerRequest.next_id) {
          navigate(`/registry-control/${registerRequest.next_id}?${location.search}`);
        } else {
          navigate(`/registry-control?${location.search}`);
        }
      })
      .catch((error: any) => {
        notifyError(`Det gick inte att uppdatera registerutdraget.`);
      });
  }

  async function investigateRequest(registerRequestId: number) {
    await investigateRegisterRequest(registerRequestId)
      .then(() => notifySuccess('Registerutdraget har granskats (kräver utredning).'))
      .then(() => {
        if (registerRequest?.next_id && registerRequestId !== registerRequest.next_id) {
          navigate(`/registry-control/${registerRequest.next_id}?${location.search}`);
        } else {
          navigate(`/registry-control?${location.search}`);
        }
      })
      .catch((error: any) => {
        notifyError(`Det gick inte att uppdatera registerutdraget.`);
      });
  }

  async function resetRequest(registerRequestId: number) {
    await resetRegisterRequest(registerRequestId)
      .then(() => notifySuccess('Registerutdraget har återställts.'))
      .then(() => {
        if (registerRequest?.next_id && registerRequestId !== registerRequest.next_id) {
          navigate(`/registry-control/${registerRequest.next_id}?${location.search}`);
        } else {
          navigate(`/registry-control?${location.search}`);
        }
      })
      .catch((error: any) => {
        notifyError(`Det gick inte att återställa registerutdraget.`);
      });
  }

  if (loading) {
    return <div className="p-6 text-slate-600">Loading register request...</div>;
  }

  if (error || !registerRequest) {
    return <div className="p-6 text-red-600">{error || 'Register request not found'}</div>;
  }

  function assessmentToText(registerRequest: RegisterRequestShowInterface): string {
    if (
      registerRequest.assessment === 'Godkänd' &&
      registerRequest.category &&
      [
        'Undantagen (giltig RU)',
        'Undantagen (under 14)',
        'Undantagen (under 15)',
        'Undantagen (avtalspart)',
        'Undantagen (migration)',
      ].includes(registerRequest.category)
    ) {
      return registerRequest.category;
    } else {
      return registerRequest.assessment;
    }
  }

  function eventTypeToText({ event_type, metadata }: RegisterRequestHistoryInterface): string {
    switch (event_type) {
      case 'created':
        return 'Begäran skapad';
      case 'file_uploaded':
        return 'Fil uppladdad';
      case 'file_deleted':
        return 'Fil raderad';
      case 'file_deleted_automatically':
        return `Fil raderad automatiskt av systemet efter ${metadata?.retention_days} dagar`;
      case 'not_handled':
        return 'Begäran kompletterades av användaren (fil uppladdad)';
      case 'requires_completion':
        return 'Begäran granskad (komplettering begärd)';
      case 'approved':
        return 'Begäran godkänd';
      case 'investigation':
        return 'Begäran granskad (kräver utredning)';
      case 'reset':
        return 'Begäran återställd';
      case 'invalid_or_deleted':
        return 'Begäran ogiltig/raderad';
      default:
        return 'Okänd händelse';
    }
  }

  return (
    <>
      {/* Title */}
      <div className="flex items-center justify-between h-18 pt-3 mx-8 mb-5">
        <div className="flex items-center">
          <BackLink link={`/registry-control${location.search}`} goBack={false} />
          <h1>
            <span className="text-2xl font-semibold">Registerkontroll</span>{' '}
            <span className="text-neutral-600 text-xl">
              av: {registerRequest.user.first_name} {registerRequest.user.surname} (nr{' '}
              {registerRequest.user.employment_nr})
            </span>
          </h1>
        </div>
        <div className="flex items-center">
          {registerRequest.previous_id && (
            <Button
              style={{
                maxHeight: '48px',
                minHeight: '48px',
                maxWidth: '48px',
                minWidth: '48px',
              }}
              component={Link}
              to={`/registry-control/${registerRequest.previous_id}${location.search}`}
            >
              <ArrowCircleUpOutlinedIcon fontSize="large" className="text-neutral-600" />
            </Button>
          )}
          {registerRequest.next_id && (
            <Button
              style={{
                maxHeight: '48px',
                minHeight: '48px',
                maxWidth: '48px',
                minWidth: '48px',
              }}
              component={Link}
              to={`/registry-control/${registerRequest.next_id}${location.search}`}
            >
              <ArrowCircleDownOutlinedIcon fontSize="large" className="text-neutral-600" />
            </Button>
          )}
        </div>
      </div>
      <div className="px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="px-4">
            <div className="grid gap-4 mt-1">
              <InfoRow label="Personnummer:" value={registerRequest.user.person_nr} strong />
              <InfoRow label="Status begäran:" value={assessmentToText(registerRequest)} strong />
              <InfoRow label="Orsak för begäran:" value={registerRequest.reason} strong />
            </div>

            <Formik
              initialValues={getDefaultsWithActualValues(registerRequestInitialValues, {
                ...registerRequest,
                valid_from: dayjs(registerRequest.valid_from, 'YYYY-MM-DD'),
              })}
              validationSchema={Yup.object({
                valid_from: Yup.mixed()
                  .test('required', '* Required', (value) => !!value)
                  .test('valid-date', 'Måste vara ett giltigt datum', (value) => value?.isValid())
                  .test('not-future', 'Kan inte vara i framtiden', (value) =>
                    value?.isBefore(dayjs().add(1, 'day'), 'date')
                  ),
              })}
              onSubmit={(values, { setSubmitting }): void => {
                handleSubmit(values, setSubmitting);
              }}
            >
              {({ values, errors, handleChange, setFieldValue, handleSubmit, isSubmitting, isValid, dirty }) => {
                const oldValidFromError = isOlderThanSixMonths(values.valid_from)
                  ? 'Utfärdandedatumet är mer än 6 månader gammalt'
                  : null;

                return (
                  <form onSubmit={handleSubmit}>
                    <div className="text-xl text-neutral-600 font-bold mt-8">Kontroll av uppgifter</div>
                    <div className="mt-4 text-neutral-600 text-sm">Kontrollera personnummer</div>
                    <div className="mt-2">
                      <Button
                        className="h-13"
                        variant={personNrCorrect ? 'contained' : 'outlined'}
                        color="primary"
                        disabled={!registerRequest.editable || isSubmitting}
                        endIcon={personNrCorrect ? <Handshake /> : <HandshakeOutlined />}
                        onClick={() => setPersonNrCorrect((prev) => !prev)}
                        sx={{
                          '&.MuiButton-contained.Mui-disabled': {
                            backgroundColor: '#43967680',
                            color: 'white',
                            opacity: 1,
                          },
                        }}
                      >
                        Stämmer
                      </Button>
                    </div>
                    <div className="mt-6 text-neutral-600 text-sm">Ange registerutdragets utfärdatdatum</div>
                    <div className="mt-4">
                      <FormFreeDateField
                        label="Datum utfärdat"
                        onlyValid={false}
                        size={null}
                        value={values.valid_from}
                        error={errors?.valid_from ?? oldValidFromError}
                        maxDate={dayjs()}
                        disabled={!registerRequest.editable || isSubmitting}
                        onChange={(day) => {
                          setFieldValue('valid_from', day);
                          setValidFrom(day);
                          setOldValidFrom(isOlderThanSixMonths(day));
                        }}
                        sx={{
                          '& .MuiIconButton-root': {
                            color: '#439676 !important',
                          },
                          '& .Mui-disabled .MuiOutlinedInput-input': {
                            WebkitTextFillColor: '#43967680 !important',
                          },
                          '& .MuiOutlinedInput-input': {
                            WebkitTextFillColor: '#439676 !important',
                          },
                          '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#43967680 !important',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#439676 !important',
                          },
                          '& .Mui-disabled.MuiInputLabel-root': {
                            color: '#43967680 !important',
                          },
                          '& .MuiInputLabel-root': {
                            color: '#439676 !important',
                          },
                        }}
                      />
                    </div>

                    <div className="mt-6 text-neutral-600 text-sm">Kontrollera utdragets innehåll</div>
                    <div className="mt-2 grid gap-6 grid-cols-3">
                      <Button
                        className="h-13"
                        variant="contained"
                        color="primary"
                        disabled={!personNrCorrect || !isValid || isSubmitting || !registerRequest.editable}
                        endIcon={<ThumbUpAltOutlined />}
                        type="submit"
                        sx={{
                          '&.Mui-disabled': {
                            backgroundColor: '#43967680',
                            color: 'white',
                            opacity: 1,
                          },
                        }}
                      >
                        Godkänn
                      </Button>

                      <div
                        className={`flex items-center ${
                          !!registerRequest.register_request_emails?.length ? 'gap-2' : ''
                        }`}
                      >
                        <Button
                          fullWidth={true}
                          className="h-13"
                          variant="outlined"
                          color="error"
                          endIcon={<ForumOutlined />}
                          disabled={['Komplettering'].includes(registerRequest.assessment) || !registerRequest.editable}
                          onClick={(): void => {
                            setRequireCompletionDialogVisible(true);
                          }}
                        >
                          Komplettering
                        </Button>
                      </div>

                      <Button
                        variant="contained"
                        color="error"
                        endIcon={<GavelOutlined />}
                        disabled={['Utredning'].includes(registerRequest.assessment) || !registerRequest.editable}
                        onClick={(): void => {
                          setInvestigationDialogVisible(true);
                        }}
                      >
                        Utredning
                      </Button>
                    </div>
                    {(registerRequest?.next_id ||
                      ['Godkänd', 'Komplettering', 'Utredning'].includes(registerRequest.assessment)) && (
                      <div className="grid grid-cols-3 items-center mt-8">
                        <div className="col-start-2 justify-self-center">
                          {registerRequest?.next_id && (
                            <a
                              className="text-menuGreen hover:underline"
                              href={`/registry-control/${registerRequest.next_id}${location.search}`}
                            >
                              Hoppa över
                            </a>
                          )}
                        </div>

                        <div className="col-start-3 justify-self-end">
                          {['Godkänd', 'Komplettering', 'Utredning'].includes(registerRequest.assessment) && (
                            <Tooltip
                              title={
                                registerRequest.editable
                                  ? 'Återställ RegU-begäran'
                                  : 'RegU-begäran automatiskt arkiverad'
                              }
                            >
                              <span>
                                <Button
                                  disabled={!registerRequest.editable}
                                  type="button"
                                  onClick={(): void => {
                                    setResetDialogVisible(true);
                                  }}
                                  className="flex items-center justify-center rounded p-1 hover:bg-gray-100"
                                  aria-label="Reset"
                                >
                                  <RestartAltOutlinedIcon fontSize="medium" />
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}
                  </form>
                );
              }}
            </Formik>

            <div className="flex mt-8 gap-x-4">
              <h1 className="text-xl text-neutral-600 font-bold ">Historik</h1>

              <Tooltip title={'Kompletteringshistorik'} placement="bottom">
                <button
                  type="button"
                  className={`${!registerRequest.register_request_emails?.length ? 'text-gray-400' : 'text-menuGreen'}`}
                  disabled={!registerRequest.register_request_emails?.length}
                  onClick={() => setEmailHistoryDialogVisible(true)}
                >
                  <Badge badgeContent={registerRequest.register_request_emails.length} max={9} color="error">
                    <MailOutlinedIcon fontSize="small" />
                  </Badge>
                </button>
              </Tooltip>
            </div>

            <div className="mt-2 grid rounded-md border border-neutral-400 bg-neutral-100 text-neutral-600 gap-y-6 p-4">
              {registerRequest.histories.map((history, index) => (
                <div key={index} className="grid grid-cols-[40fr_15fr]">
                  <span className="text-left">{eventTypeToText(history)}</span>

                  <span className="text-right">{history.created_at}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <FileViewer
              registerRequestId={registerRequestId}
              files={registerRequest.register_request_uploads}
              setAllFilesChecked={setAllFilesChecked}
            />
          </div>
        </div>
      </div>
      <ApproveWarningDialog
        isVisible={approveWarningDialogVisible}
        onClose={(): void => {
          setApproveWarningDialogVisible(false);
        }}
        onConfirm={(): void => {
          setApproveWarningDialogVisible(false);
          approveRequest(registerRequestId, validFrom?.format('YYYY-MM-DD'));
        }}
        message={
          <div>
            {!allFilesChecked && (
              <>
                <p>
                  Du har inte öppnat samtliga {registerRequest.register_request_uploads.length} filer som laddats upp.
                </p>
              </>
            )}
            {oldValidFrom && (
              <>
                <p className={!allFilesChecked ? 'mt-4' : ''}>
                  Utfärdandedatumet var mer än 6 månader gammalt vid uppladdningstillfället.
                </p>
              </>
            )}
            <p className="mt-4 font-bold text-neutral-600">Vill du trots detta godkänna intyget?</p>
          </div>
        }
      />
      <RequireCompletionDialog
        isVisible={requireCompletionDialogVisible}
        onClose={(): void => {
          setRequireCompletionDialogVisible(false);
        }}
        onConfirm={acceptRequireCompletion}
        user={registerRequest.user}
      />
      <InvestigationDialog
        isVisible={investigationDialogVisible}
        onClose={(): void => {
          setInvestigationDialogVisible(false);
        }}
        onConfirm={(): void => {
          setInvestigationDialogVisible(false);
          investigateRequest(registerRequestId);
        }}
        message={
          <div>
            <p>Markeras som under utredning.</p>
            {registerRequest.operational_manager && (
              <>
                <p className="mt-4">
                  Ansvarig VA: {registerRequest.operational_manager.first_name}{' '}
                  {registerRequest.operational_manager.surname}
                </p>
                <p className="mt-4">
                  Mejladress:{' '}
                  <a href={`mailto:${registerRequest.operational_manager.email}`}>
                    {registerRequest.operational_manager.email}
                  </a>
                </p>
              </>
            )}
            <p className="mt-4">Känsliga uppgifter får endast mailas mellan @ungomsorg.se -adresser</p>
          </div>
        }
      />
      <ResetDialog
        isVisible={resetDialogVisible}
        onClose={(): void => {
          setResetDialogVisible(false);
        }}
        onConfirm={(): void => {
          setResetDialogVisible(false);
          resetRequest(registerRequestId);
        }}
      />
      <RegisterRequestEmailHistoryDialog
        isVisible={emailHistoryDialogVisible}
        onClose={() => setEmailHistoryDialogVisible(false)}
        emails={registerRequest.register_request_emails}
      />
    </>
  );
}
